import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Check, X, Clock, CheckCheck } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, approvalTasks, type ApprovalTask } from '@/lib/mocks'
import { CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/states'
import { PageHeader, AgentBadge, ConfidencePill } from '@/lib/atsUi'
import { DataList } from '@/components/ui/data-list'

export const Route = createFileRoute('/inbox')({ component: InboxPage })

function InboxPage() {
  const [tasks, setTasks] = useState<ApprovalTask[]>(approvalTasks)

  function approve(id: string) {
    setTasks((prev) => prev.filter((t) => t.id !== id))
    toast.success('Approved — workflow resumed')
  }

  function reject(id: string) {
    setTasks((prev) => prev.filter((t) => t.id !== id))
    toast('Sent back to the agent')
  }

  function approveAll() {
    const n = tasks.length
    if (n === 0) return
    setTasks([])
    toast.success(`Approved all ${n} — workflows resumed`)
  }

  return (
    <DashboardLayout
      title="Approvals"
      navSections={atsNav}
      tenant={currentTenant}
      user={currentUser}
    >
      <PageHeader
        title="Approvals"
        subtitle="Work the agents prepared — approve or send back."
        action={
          tasks.length > 0 ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground tabular-nums">{tasks.length} pending</span>
              <Button size="sm" onClick={approveAll}>
                <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
                Approve all
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="mt-8">
        <DataList
          items={tasks}
          getKey={(t) => t.id}
          pageSize={5}
          empty={
            <EmptyState
              title="All clear"
              description="No pending approvals — the agents will queue new work here."
            />
          }
          renderRow={(task) => (
            <TaskRow
              task={task}
              onApprove={() => approve(task.id)}
              onReject={() => reject(task.id)}
            />
          )}
        />
      </div>
    </DashboardLayout>
  )
}

interface TaskRowProps {
  task: ApprovalTask
  onApprove: () => void
  onReject: () => void
}

function TaskRow({ task, onApprove, onReject }: TaskRowProps) {
  const meta = [task.jobTitle, task.client, task.count != null ? `${task.count} candidates` : null]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="px-5 py-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <AgentBadge agent={task.agent} />
            <span className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">
              {task.step}
            </span>
            <ConfidencePill score={task.confidence} />
          </div>
          <span className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {task.createdAt}
          </span>
        </div>

        <p className="text-sm font-medium text-foreground leading-snug">{task.title}</p>
        <p className="text-sm text-muted-foreground leading-relaxed">{task.summary}</p>
        {meta && (
          <p className="text-xs text-muted-foreground">{meta}</p>
        )}
      </div>

      <CardFooter className="flex items-center gap-2 pt-0 pb-0 flex-wrap border-t border-border mt-3 px-0">
        <div className="flex items-center gap-2 flex-wrap pt-3">
          <Button size="sm" variant="default" onClick={onApprove}>
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Approve
          </Button>
          <Button size="sm" variant="destructive" onClick={onReject}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Send back
          </Button>
        </div>
      </CardFooter>
    </div>
  )
}
